import React, { useLayoutEffect, useMemo, useRef } from "react";

import { SMSTemplate, Order, ExecutorLocations, Car } from "../../services";
import { store, useTypedDispatch, useTypedSelector } from "../../redux/store";
import orderPage from "../../redux/reducers/OrdersPage";
import { globalState } from "../../redux/reducers";
import getPolygons from "../../redux/services/Map/polygons";
import { ordersAction as orders } from "../../redux/reducers/Orders";
import { MessageTemplateActions } from "../../types/MessageTemplateEnums";
import {
	useModelSubscribe,
	useTaxiServiceFilterAccessControl,
} from "../../hooks";
import { sameExecutorLocationPoints } from "../../utils/ordersHotPath";
import {
	gpsStaleTickMsFromModels,
	minExecutorCoordinatesTtlMs,
} from "../MapExecutorLocations/gpsFresh.util";
import PerfProfiler from "../../tests/PerfProfiler";

import { OrdersDataLoader, ChatMessageLoader } from "./components";

const ORDER_SMS_TEMPLATES = [
	MessageTemplateActions.WILL_BE_TIME,
	MessageTemplateActions.OUTPUT_PASSENGERS,
	MessageTemplateActions.LATE,
	MessageTemplateActions.CAR_NOT_FOUND,
	MessageTemplateActions.ORDER_SUCCESSFULLY_COMPLETED,
	MessageTemplateActions.WITH_PASSENGERS,
	MessageTemplateActions.DRIVER_SWITCHED,
];

const OrderPageModelsLoader: React.FC<OrderPageModelsLoader.Props> = () => {
	const dispatch = useTypedDispatch();

	const { accessTaxiServicesIds } =
		useTaxiServiceFilterAccessControl<Order.Model>();

	const connected = useTypedSelector((state) => state.prpc.connected);
	const authorized = useTypedSelector((state) => state.prpc.authorized);
	const lang = useTypedSelector((state) => state.session.language);

	const optionsCarService = useMemo<Car.SubscribeOptions>(() => {
		const result: Car.SubscribeOptions = {
			language: lang,
			taxiServiceIds: accessTaxiServicesIds,
			subscribeType: "main_order_page",
		};

		return result;
	}, [accessTaxiServicesIds, lang]);

	const { models: CarSub } = useModelSubscribe(optionsCarService, Car);
	const smsTemplatesSubscription = useModelSubscribe(
		{
			actions: ORDER_SMS_TEMPLATES,
			order: { action: "ASC" },
		} as any,
		SMSTemplate,
	);
	const optionsExecutorLocations =
		useMemo<ExecutorLocations.SubscribeOptions>(
			() => ({
				revealExecutor: true,
				taxiServiceIds: accessTaxiServicesIds,
			}),
			[accessTaxiServicesIds],
		);
	const subscribeExecutorLocations = useModelSubscribe(
		optionsExecutorLocations,
		ExecutorLocations,
	);

	const canSubscribe: boolean = useMemo(
		() => connected && authorized,
		[connected, authorized],
	);

	// --------------------------------

	useLayoutEffect(() => {
		dispatch(getPolygons());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const smsTemplatesModels = useMemo(
		() => (canSubscribe ? smsTemplatesSubscription.models : []),
		[canSubscribe, smsTemplatesSubscription.models],
	);

	const prevSms = useRef(smsTemplatesModels);
	useLayoutEffect(() => {
		if (prevSms.current === smsTemplatesModels) return;
		prevSms.current = smsTemplatesModels;
		dispatch(orderPage.actions.setSMSTemplates(smsTemplatesModels));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [smsTemplatesModels]);

	const executorLocationModels = useMemo(
		() => (canSubscribe ? subscribeExecutorLocations.models : []),
		[canSubscribe, subscribeExecutorLocations.models],
	);

	const prevLocations = useRef(executorLocationModels);
	useLayoutEffect(() => {
		const prev = prevLocations.current;
		const next = executorLocationModels;
		const { models: taxiServiceModels } =
			store.getState().taxiServices || {};
		const taxiServices = taxiServiceModels || [];
		if (
			sameExecutorLocationPoints(prev, next, {
				now: Date.now(),
				ttlMs: minExecutorCoordinatesTtlMs(taxiServices),
				refreshAfterMs: gpsStaleTickMsFromModels(taxiServices),
			})
		)
			return;
		prevLocations.current = next;
		dispatch(
			globalState.executorLocations.data.setSubscriptionBasicData(
				executorLocationModels,
			),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [executorLocationModels]);

	const carModels = useMemo(
		() => (canSubscribe ? CarSub : []),
		[canSubscribe, CarSub],
	);

	const prevCars = useRef(carModels);
	useLayoutEffect(() => {
		if (prevCars.current === carModels) return;
		prevCars.current = carModels;
		dispatch(orders.map.data.setCarsData(carModels));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [carModels]);

	return (
		<>
			<PerfProfiler id="OrdersLoader">
				<OrdersDataLoader carModels={carModels} />
			</PerfProfiler>
			<ChatMessageLoader />
			{/* <ParkingLoader /> */}
		</>
	);
};

declare namespace OrderPageModelsLoader {
	interface Props {}
}

export default OrderPageModelsLoader;
